import {
  mapSocialLinksToForm,
  buildSocialLinksPayload,
  toOptionalUrl,
} from './socialLinks';

const parsePortfolioCaption = (caption = "") => {
  const [title, ...rest] = caption.split(" - ");
  if (rest.length === 0) {
    return { title: caption, category: "" };
  }
  return { title: title.trim(), category: rest.join(" - ").trim() };
};

const isValidHttpUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const toOptionalRate = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed);
};

export const mapPortfolioToProPage = (payload, fallbackUser = {}) => {
  const data = payload?.portfolioData ?? payload ?? {};
  const user = payload?.user ?? fallbackUser;
  const socialLinks = data.contact?.socialLinks ?? payload?.social_links ?? {};

  const portfolio = (data.portfolio ?? []).map((item, index) => {
    const parsed = parsePortfolioCaption(item.caption ?? "");
    return {
      id: item.id ?? `photo_${index}`,
      title: parsed.title,
      category: parsed.category,
      description: item.description ?? '',
      image_url: item.imageUrl ?? item.image_url ?? '',
      publicId: item.publicId ?? item.public_id ?? `external_${item.id ?? index}`,
    };
  });

  return {
    slug: payload?.slug ?? user.id ?? "",
    is_published: Boolean(
      payload?.is_published ?? data.meta?.publishedAt
    ),
    cover_image_url: data.coverImageUrl ?? payload?.cover_image_url ?? "",
    profile_image_url: data.profileImageUrl ?? payload?.profile_image_url ?? user.photoUrl ?? "",
    headline: data.headline ?? "",
    bio: data.description ?? "",
    tarif_journalier: data.pricing?.dailyRate ?? "",
    tarif_horaire: data.pricing?.hourlyRate ?? "",
    competences: data.skills ?? [],
    langues: data.languages ?? [],
    experiences: (data.experiences ?? []).map((exp) => ({
      title: exp.title ?? "",
      company: exp.company ?? "",
      start_date: exp.startDate ?? exp.start_date ?? "",
      end_date: exp.endDate ?? exp.end_date ?? "",
      description: exp.description ?? "",
    })),
    formations: (data.education ?? []).map((edu) => ({
      title: edu.title ?? "",
      school: edu.school ?? "",
      year: edu.year ?? "",
      description: edu.description ?? "",
    })),
    ...mapSocialLinksToForm(socialLinks),
    social_links: socialLinks,
    website_url: data.contact?.website ?? "",
    show_contact_form: data.settings?.showContactForm !== false,
    show_tarifs: data.settings?.showTarifs !== false,
    portfolio,
    user,
  };
};

export const mapProPageFormToPortfolio = (formData, existingPortfolio = []) => {
  const portfolioItems = (existingPortfolio ?? [])
    .map((item, index) => {
      const imageUrl = item.image_url ?? item.imageUrl;
      if (!isValidHttpUrl(imageUrl)) return null;

      return {
        id: item.id ?? `photo_${index}`,
        imageUrl,
        publicId: item.publicId ?? item.public_id ?? `external_${item.id ?? index}`,
        caption: [item.title, item.category].filter(Boolean).join(' - ') || item.title || undefined,
        order: index,
      };
    })
    .filter(Boolean);

  const websiteUrl = toOptionalUrl(formData.website_url);
  const socialLinks = buildSocialLinksPayload(formData);

  return {
    headline: formData.headline ?? "",
    description: formData.bio ?? "",
    coverImageUrl: toOptionalUrl(formData.cover_image_url),
    profileImageUrl: toOptionalUrl(formData.profile_image_url),
    skills: formData.competences ?? [],
    languages: formData.langues ?? [],
    pricing: {
      dailyRate: toOptionalRate(formData.tarif_journalier),
      hourlyRate: toOptionalRate(formData.tarif_horaire),
    },
    settings: {
      showContactForm: formData.show_contact_form !== false,
      showTarifs: formData.show_tarifs !== false,
    },
    contact: {
      website: websiteUrl,
      socialLinks,
    },
    experiences: (formData.experiences ?? []).map((exp, index) => ({
      id: exp.id ?? `exp_${index}`,
      title: exp.title,
      company: exp.company,
      startDate: exp.start_date ?? exp.startDate ?? "",
      endDate: exp.end_date ?? exp.endDate ?? "",
      description: exp.description ?? "",
    })),
    education: (formData.formations ?? []).map((edu, index) => ({
      id: edu.id ?? `edu_${index}`,
      title: edu.title,
      school: edu.school,
      year: edu.year ?? "",
      description: edu.description ?? "",
    })),
    portfolio: portfolioItems,
  };
};

export const mapNewPortfolioItemToPhoto = (item, order = 0) => {
  const id = `photo_${Date.now()}_${order}`;
  return {
    id,
    imageUrl: item.image_url,
    publicId: `external_${id}`,
    caption: [item.title, item.category].filter(Boolean).join(" - ") || item.title,
    order,
  };
};

export const getApiErrorMessage = (error, fallback) => {
  const details = error?.response?.data?.error?.details
    || error?.details
    || [];

  if (details.length > 0) {
    return details.map((item) => item.message).join(' · ');
  }

  return error?.response?.data?.error?.message
    || error?.response?.data?.message
    || error?.response?.data?.detail
    || error?.message
    || fallback;
};

export const unwrapApiData = (response) => response?.data?.data ?? response?.data ?? response;

export const createEmptySocialFormFields = () => mapSocialLinksToForm({});
